import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuyspread.click';

export default function ArticlePage() {
  const { t } = useTranslation();

  const faqData = [
    {
      question: 'Ist das Oopbuy Spreadsheet kostenlos?',
      answer: 'Ja, das Oopbuy Spreadsheet ist vollständig kostenlos. Es wird von der Community gepflegt und steht allen Nutzern ohne Einschränkungen zur Verfügung.'
    },
    {
      question: 'Wie oft wird das Oopbuy Spreadsheet aktualisiert?',
      answer: 'Das Spreadsheet wird täglich aktualisiert. Neue Produkte werden hinzugefügt, veraltete Einträge entfernt und Preise sowie QC-Bewertungen kontinuierlich angepasst.'
    },
    {
      question: 'Kann ich selbst Produkte zum Spreadsheet hinzufügen?',
      answer: 'Ja, die Community lebt von aktiven Beiträgen. Erfahrene Nutzer können neue Produkte vorschlagen, die dann von Moderatoren geprüft und freigeschaltet werden.'
    },
    {
      question: 'Sind alle Verkäufer im Spreadsheet vertrauenswürdig?',
      answer: 'Die im Spreadsheet gelisteten Verkäufer werden von der Community geprüft und bewertet. Dennoch empfehlen wir, vor jedem Kauf die aktuellen Bewertungen und QC-Notizen sorgfältig zu lesen.'
    },
    {
      question: 'Funktioniert das Spreadsheet auch auf dem Smartphone?',
      answer: 'Ja, das Oopbuy Spreadsheet ist mit allen gängigen Tabellenkalkulations-Apps kompatibel und kann problemlos auf Smartphones und Tablets genutzt werden.'
    },
  ];

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Schema.org structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Startseite", "item": `${siteUrl}/` },
            { "@type": "ListItem", "position": 2, "name": "Oopbuy Spreadsheet Guide", "item": `${siteUrl}/oopbuy-spreadsheet-guide` }
          ]
        })
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "Der ultimative Oopbuy Spreadsheet Guide für Deutschland 2026 – Alles, was du wissen musst",
          "description": "Vollständiger Guide zum Oopbuy Spreadsheet mit QC-Erklärungen, Versandinfos und Vergleichstabelle.",
          "datePublished": "2026-07-15",
          "dateModified": "2026-07-16",
          "author": { "@type": "Organization", "name": "Oopbuy Spreadsheet Redaktion" },
          "publisher": { "@type": "Organization", "name": "Oopbuy Spreadsheet", "url": siteUrl },
          "inLanguage": "de"
        })
      }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqData.map((faq) => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
          }))
        })
      }} />

      {/* Article Header */}
      <section className="pt-24 md:pt-28 pb-8 md:pb-12 px-4 md:px-10">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6" aria-label="breadcrumb">
            <Link to="/" className="hover:text-foreground-700 transition-colors cursor-pointer">{t('article.breadcrumb_home')}</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-600">{t('article.breadcrumb_article')}</span>
          </nav>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">{t('article.category')}</span>
            <span className="text-xs text-foreground-400">{t('article.reading_time')}</span>
            <span className="text-xs text-foreground-400">{t('article.published')}</span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            Der ultimative Oopbuy Spreadsheet Guide für Deutschland 2026 – Alles, was du wissen musst
          </h1>

          <p className="text-sm text-foreground-500 mb-8">{t('article.updated')}</p>

          {/* Author */}
          <div className="flex items-center gap-3 pb-8 border-b border-background-200">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-accent-100">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20professional%20editorial%20portrait%20of%20european%20person%20on%20soft%20warm%20taupe%20background%2C%20gentle%20natural%20daylight%2C%20composed%20confident%20expression%2C%20clean%20minimal%20headshot%20composition%2C%20refined%20scandinavian%20style&width=80&height=80&seq=author-v2-avatar-f1&orientation=squarish"
                alt={t('article.author_name')}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground-800">{t('article.author_name')}</p>
              <p className="text-xs text-foreground-500">{t('article.author_role')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="px-4 md:px-10 pb-16">
        <div className="max-w-3xl mx-auto">
          <div className="prose-custom text-foreground-700 leading-relaxed space-y-8">

            {/* TOC */}
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-10">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4">Inhaltsverzeichnis</h2>
              <ol className="space-y-2 text-sm text-foreground-600 list-decimal list-inside">
                <li>Was ist das Oopbuy Spreadsheet?</li>
                <li>Die Geschichte und Entwicklung des Spreadsheets</li>
                <li>Warum das Oopbuy Spreadsheet in Deutschland so beliebt ist</li>
                <li>Die wichtigsten Kategorien im Überblick</li>
                <li>So nutzt du das Oopbuy Spreadsheet effektiv</li>
                <li>QC und Qualitätskontrolle verstehen</li>
                <li>Versand und Lieferung nach Deutschland</li>
                <li>Oopbuy Spreadsheet vs. Alternativen</li>
                <li>Häufig gestellte Fragen (FAQ)</li>
              </ol>
            </div>

            {/* Section 1 */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                1. Was ist das Oopbuy Spreadsheet?
              </h2>
              <p className="text-sm md:text-base mb-4">
                Das <strong>Oopbuy Spreadsheet</strong> ist eine sorgfältig kuratierte, ständig aktualisierte Tabelle, die tausende von Produkten aus verschiedenen Kategorien zusammen mit vertrauenswürdigen Verkäuferinformationen, Qualitätsbewertungen und direkten Kauflinks auflistet. Es wurde von einer engagierten Community entwickelt, um den Online-Einkaufsprozess für deutsche Käufer erheblich zu vereinfachen.
              </p>
              <p className="text-sm md:text-base mb-4">
                Anders als herkömmliche Produktsuchmaschinen bietet das Oopbuy Spreadsheet eine strukturierte Übersicht, bei der jedes Produkt von echten Nutzern geprüft und bewertet wurde. Dies eliminiert das Risiko, auf minderwertige Waren oder unzuverlässige Verkäufer hereinzufallen – ein Problem, das im internationalen Online-Handel leider allzu häufig vorkommt.
              </p>
              <p className="text-sm md:text-base">
                Für den deutschen Markt ist das Oopbuy Spreadsheet besonders wertvoll, da es spezifische Informationen zu EU-Versand, Zollbestimmungen und Lieferzeiten nach Deutschland enthält. So können Käufer sicher sein, dass ihre Bestellungen ohne versteckte Kosten oder unerwartete Verzögerungen ankommen.
              </p>
            </section>

            {/* Featured Image */}
            <div className="rounded-2xl overflow-hidden aspect-[16/7] my-10">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20workspace%20with%20large%20monitor%20showing%20organized%20spreadsheet%20interface%20on%20warm%20sand%20colored%20desk%2C%20soft%20natural%20daylight%20through%20window%2C%20clean%20refined%20scandinavian%20aesthetic%2C%20warm%20neutral%20tones%2C%20editorial%20productivity%20photography%2C%20professional%20calm%20atmosphere%2C%20organized%20desk%20with%20minimal%20accessories&width=1200&height=525&seq=article-v2-img-f2&orientation=landscape"
                alt="Oopbuy Spreadsheet Übersicht – strukturierte Tabelle mit Produktinformationen und Bewertungen auf einem modernen Monitor"
                title="Oopbuy Spreadsheet Übersicht"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
            </div>

            {/* Section 2 */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                2. Die Geschichte und Entwicklung des Spreadsheets
              </h2>
              <p className="text-sm md:text-base mb-4">
                Das Oopbuy Spreadsheet entstand aus der Notwendigkeit heraus, eine zentrale Anlaufstelle für verifizierte Produktinformationen zu schaffen. Was als kleines Community-Projekt begann, hat sich innerhalb weniger Jahre zu einem der umfassendsten und vertrauenswürdigsten Verzeichnisse im deutschsprachigen Raum entwickelt.
              </p>
              <p className="text-sm md:text-base mb-4">
                Die erste Version des Spreadsheets umfasste lediglich einige hundert Einträge in wenigen Kategorien. Heute, im Jahr 2026, beherbergt das Oopbuy Spreadsheet über <strong>10.000 kuratierte Produkte</strong> in 14 verschiedenen Kategorien – von Schuhen und Bekleidung über Accessoires bis hin zu Parfums und vielem mehr.
              </p>
              <p className="text-sm md:text-base">
                Der Schlüssel zum Erfolg liegt in der aktiven Community. Deutsche Nutzer tragen kontinuierlich neue Funde bei, aktualisieren Preise und teilen ihre Erfahrungen mit bestimmten Verkäufern. Diese kollektive Intelligenz sorgt dafür, dass das Spreadsheet stets auf dem neuesten Stand bleibt und eine unvergleichliche Datenqualität bietet.
              </p>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                3. Warum das Oopbuy Spreadsheet in Deutschland so beliebt ist
              </h2>
              <p className="text-sm md:text-base mb-4">
                Die Beliebtheit des Oopbuy Spreadsheets in Deutschland lässt sich auf mehrere entscheidende Faktoren zurückführen. An erster Stelle steht die <strong>enorme Zeitersparnis</strong>. Statt stundenlang durch verschiedene Plattformen und Shops zu navigieren, finden Nutzer alle relevanten Informationen gebündelt an einem Ort.
              </p>
              <p className="text-sm md:text-base mb-4">
                Ein weiterer entscheidender Vorteil ist die <strong>Transparenz und Vertrauenswürdigkeit</strong>. Jeder Eintrag im Oopbuy Spreadsheet enthält detaillierte Qualitätsbewertungen (QC), echte Nutzerfotos und aktuelle Preisinformationen. Diese Offenheit schafft Vertrauen und minimiert das Risiko von Fehlkäufen erheblich.
              </p>
              <p className="text-sm md:text-base mb-4">
                Besonders geschätzt wird auch die <strong>deutschlandspezifische Ausrichtung</strong>. Das Spreadsheet berücksichtigt EU-Zollbestimmungen, gängige Versandrouten nach Deutschland und typische Lieferzeiten. Für deutsche Käufer bedeutet dies: keine bösen Überraschungen beim Import und volle Kostenkontrolle von Anfang an.
              </p>
              <p className="text-sm md:text-base">
                Nicht zuletzt spielt die <strong>Community-Komponente</strong> eine große Rolle. Der Austausch mit Gleichgesinnten, das Teilen von Entdeckungen und die gemeinsame Qualitätssicherung machen das Oopbuy Spreadsheet zu mehr als nur einer Tabelle – es ist eine lebendige Plattform für informierte Kaufentscheidungen.
              </p>
            </section>

            {/* Section 4 - Categories */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                4. Die wichtigsten Kategorien im Überblick
              </h2>
              <p className="text-sm md:text-base mb-6">
                Das Oopbuy Spreadsheet deckt eine beeindruckende Bandbreite an Produktkategorien ab. Hier ein detaillierter Überblick über alle 14 Kategorien und was sie besonders macht:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 text-xs font-bold">
                      {cat.id.toString().slice(-2)}
                    </span>
                    <span className="text-sm font-medium text-foreground-800 flex-1">{t(cat.nameKey)}</span>
                    <i className="ri-arrow-right-up-line text-sm text-foreground-400 group-hover:text-foreground-700 transition-colors"></i>
                  </a>
                ))}
              </div>

              <p className="text-sm md:text-base">
                Jede Kategorie wird von erfahrenen Community-Mitgliedern gepflegt, die regelmäßig neue Produkte hinzufügen, veraltete Einträge entfernen und die Qualitätsbewertungen auf dem neuesten Stand halten. Diese dezentrale, aber koordinierte Pflege garantiert eine konstant hohe Datenqualität über alle Kategorien hinweg.
              </p>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                5. So nutzt du das Oopbuy Spreadsheet effektiv
              </h2>
              <p className="text-sm md:text-base mb-4">
                Die effektive Nutzung des Oopbuy Spreadsheets beginnt mit dem Verständnis seiner Struktur. Die Tabelle ist in verschiedene Registerkarten (Sheets) unterteilt, die jeweils einer Produktkategorie entsprechen. Innerhalb jeder Kategorie findest du Spalten für Produktname, Verkäufer, Preis, QC-Bewertung, Versandinformationen und direkte Links.
              </p>
              <p className="text-sm md:text-base mb-4">
                <strong>Profi-Tipp:</strong> Nutze die Filterfunktion deiner Tabellenkalkulationssoftware, um gezielt nach bestimmten Produkten, Preisklassen oder Bewertungen zu suchen. So kannst du beispielsweise alle Produkte mit einer QC-Bewertung von mindestens 8/10 anzeigen lassen oder nur Einträge, die nach Deutschland versenden.
              </p>
              <p className="text-sm md:text-base">
                Für Einsteiger empfiehlt es sich, zunächst die Kategorie &quot;Schuhe&quot; oder &quot;Bekleidung&quot; zu erkunden, da diese die umfangreichsten und am besten bewerteten Einträge enthalten. Mit zunehmender Erfahrung wirst du schnell lernen, die verschiedenen Qualitätsindikatoren zu interpretieren und die für dich besten Angebote zu identifizieren.
              </p>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                6. QC und Qualitätskontrolle verstehen
              </h2>
              <p className="text-sm md:text-base mb-4">
                <strong>QC</strong> (Quality Control) ist das Herzstück des Oopbuy Spreadsheets. Jeder Produkteintrag wird von erfahrenen Community-Mitgliedern anhand standardisierter Kriterien bewertet. Diese Bewertungen umfassen Aspekte wie Materialqualität, Verarbeitung, Maßgenauigkeit und Übereinstimmung mit den Verkäuferangaben.
              </p>
              <p className="text-sm md:text-base mb-4">
                Die QC-Bewertungen folgen einer Skala von 1 bis 10, wobei 10 die höchste Qualität darstellt. Ein Wert von 8 oder höher gilt allgemein als ausgezeichnet und signalisiert, dass das Produkt die Erwartungen in nahezu allen Bereichen erfüllt oder übertrifft. Werte zwischen 6 und 7 deuten auf solide, aber nicht perfekte Qualität hin, während alles unter 6 mit Vorsicht zu genießen ist.
              </p>
              <p className="text-sm md:text-base">
                Besonders wertvoll sind die detaillierten QC-Notizen, die oft spezifische Stärken und Schwächen eines Produkts beschreiben. Diese Informationen gehen weit über einfache Sternebewertungen hinaus und ermöglichen eine wirklich informierte Kaufentscheidung.
              </p>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                7. Versand und Lieferung nach Deutschland
              </h2>
              <p className="text-sm md:text-base mb-4">
                Ein entscheidender Vorteil des Oopbuy Spreadsheets für deutsche Nutzer ist die Integration von <strong>versandspezifischen Informationen</strong>. Jeder Eintrag enthält Angaben dazu, ob der jeweilige Verkäufer nach Deutschland liefert, welche Versandoptionen verfügbar sind und mit welchen Lieferzeiten zu rechnen ist.
              </p>
              <p className="text-sm md:text-base mb-4">
                Darüber hinaus findest du im Spreadsheet wichtige Hinweise zu <strong>Zollbestimmungen und Einfuhrumsatzsteuer</strong>. Produkte mit einem Warenwert über 150 Euro unterliegen in Deutschland der Einfuhrumsatzsteuer, und das Spreadsheet kennzeichnet entsprechende Einträge, damit du die Gesamtkosten von Anfang an realistisch einschätzen kannst.
              </p>
              <p className="text-sm md:text-base">
                Viele im Spreadsheet gelistete Verkäufer bieten mittlerweile <strong>EU-spezifische Versandlinien</strong> an, die eine schnellere und zollfreiere Lieferung nach Deutschland ermöglichen. Diese Optionen sind im Spreadsheet besonders gekennzeichnet und werden von der Community regelmäßig auf Zuverlässigkeit geprüft.
              </p>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                8. Oopbuy Spreadsheet vs. Alternativen
              </h2>
              <p className="text-sm md:text-base mb-6">
                Es gibt verschiedene Alternativen zum Oopbuy Spreadsheet, aber keine bietet die gleiche Kombination aus Umfang, Aktualität und Community-basierter Qualitätskontrolle. Hier ein kurzer Vergleich:
              </p>

              <div className="overflow-x-auto rounded-xl border border-background-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-5 py-3 font-semibold text-foreground-800">Merkmal</th>
                      <th className="text-left px-5 py-3 font-semibold text-accent-700">Oopbuy Spreadsheet</th>
                      <th className="text-left px-5 py-3 font-semibold text-foreground-600">Andere Plattformen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200">
                    {[
                      ['Produktanzahl', '10.000+', 'Variiert stark'],
                      ['QC-Bewertungen', 'Detailliert & Community-basiert', 'Oft oberflächlich'],
                      ['Aktualisierungen', 'Täglich', 'Unregelmäßig'],
                      ['DE-Versandinfos', 'Integriert', 'Selten verfügbar'],
                      ['Community-Support', 'Aktiv & hilfsbereit', 'Begrenzt'],
                      ['Kosten', 'Kostenlos', 'Teilweise kostenpflichtig'],
                    ].map((row, i) => (
                      <tr key={i} className="hover:bg-background-50">
                        <td className="px-5 py-3 text-foreground-700">{row[0]}</td>
                        <td className="px-5 py-3 text-accent-700 font-medium">{row[1]}</td>
                        <td className="px-5 py-3 text-foreground-500">{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 9 - FAQ */}
            <section>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-5">
                9. Häufig gestellte Fragen (FAQ)
              </h2>

              <div className="space-y-3">
                {faqData.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200 transition-colors">
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* CTA at end */}
            <div className="bg-accent-50 rounded-2xl p-8 text-center mt-10">
              <h3 className="text-xl font-heading font-semibold text-foreground-950 mb-3">
                {t('articleDetail.cta_main_title')}
              </h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                {t('articleDetail.cta_main_text')}
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
              >
                {t('articleDetail.cta_main_button')}
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            {/* Internal links */}
            <div className="border-t border-background-200 pt-8 mt-8">
              <p className="text-xs text-foreground-400 mb-3">{t('articleDetail.back_label')}</p>
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line"></i>
                {t('article.back')}
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}